import { useSyncExternalStore } from 'react';
import { generateWith, loadRuns, saveRun, type LabInput, type LabModel, type LabResult, type LabRun } from './lab';

/**
 * The comparison that is running, or the last one shown, kept outside any
 * screen.
 *
 * A run takes a minute across fifteen models, and nobody sits still for a
 * minute. Held in the lab screen's own state it died the moment you opened
 * a queue; held here, the requests keep going, every answer still lands in
 * its column, and the screen simply picks the run back up when you return.
 * The rail reads the same store to say how far along it is.
 */

interface LabRunState {
  run: LabRun | null;
}

let state: LabRunState = { run: null };
const listeners = new Set<() => void>();

function set(next: LabRunState) {
  state = next;
  for (const l of listeners) l();
}

function update(fn: (run: LabRun) => LabRun) {
  if (state.run) set({ run: fn(state.run) });
}

export function useLabRun(): LabRun | null {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => state.run,
  );
}

/** How many columns are still waiting, for the rail. Zero when nothing runs. */
export function useLabProgress(): { done: number; total: number } | null {
  const run = useLabRun();
  if (!run) return null;
  const total = run.results.length;
  const done = run.results.filter((r) => r.status !== 'running').length;
  return done < total ? { done, total } : null;
}

export const isRunning = (run: LabRun | null) => run?.results.some((r) => r.status === 'running') ?? false;

/** Show an earlier run (from history) without running anything. */
export function showRun(run: LabRun) {
  if (isRunning(state.run)) return;
  set({ run });
}

/** Start a fresh comparison on the chosen models. Resolves when every
 *  column has an answer; callers need not await it. */
export async function startRun(models: LabModel[], input: LabInput): Promise<void> {
  if (isRunning(state.run) || models.length === 0) return;
  const fresh: LabRun = {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    at: new Date().toISOString(),
    input,
    results: models.map((m) => ({ model: m.id, status: 'running', ms: 0 })),
  };
  set({ run: fresh });

  // Every chosen model at once. Each answer lands in its own column as it
  // arrives, so a slow model never hides a fast one.
  await Promise.all(models.map(async (m) => {
    const result = await generateWith(m.id, input);
    update((run) => (run.id === fresh.id ? withResult(run, result) : run));
  }));
  if (state.run?.id === fresh.id) saveRun(state.run);
}

/** One model again, same input, without disturbing the other columns —
 *  a gateway blip should not cost a whole comparison. */
export async function retryModel(modelId: string): Promise<void> {
  const run = state.run;
  if (!run) return;
  update((r) => withResult(r, { model: modelId, status: 'running', ms: 0 }));
  const result = await generateWith(modelId, run.input);
  update((r) => (r.id === run.id ? withResult(r, result) : r));
  if (state.run?.id === run.id) saveRun(state.run);
}

function withResult(run: LabRun, result: LabResult): LabRun {
  return { ...run, results: run.results.map((r) => (r.model === result.model ? result : r)) };
}

/** Recent runs, refreshed from storage — the store owns writes, so it is
 *  the natural place to read them back. */
export const recentRuns = loadRuns;
